import { IImage } from '../../../../types/interfaces';

import { ThumbnailWidth, GalleryWidth, ZoomWidth } from './productPageDimensions';
import {
  AdjustQualityTransformation,
  GalleryPdpHeroTransformation,
  IsFourToThreeConditional,
  IsNotOneToOneConditional,
  IsWideConditional,
  ScaleTransformation,
  SideFourToThreeAdjustToOneToOneTransformation,
  ThumbnailPdpHeroTransformation,
  WideAdjustToOneToOneTransformation,
  ZoomPdpHeroTransformation,
} from './SpecificTransformations';
import { Transformation, TransformedUrl } from './Transformations';

abstract class CloudinaryTransformer {
  constructor(protected readonly _cloudinaryImage: IImage) {}

  abstract get thumbnail(): string;
  abstract get gallery(): string;
  abstract get zoom(): string;

  public get isValid(): boolean {
    return !!this._cloudinaryImage?.url;
  }
}

export class CommonCloudinaryTransformer extends CloudinaryTransformer {
  constructor(cloudinaryImage: IImage) {
    super(cloudinaryImage);
  }

  public get thumbnail(): string {
    const transformations = CommonCloudinaryTransformer.buildBaseTransformedUrl(
      this._cloudinaryImage?.url,
      new ThumbnailPdpHeroTransformation(),
      new ScaleTransformation(ThumbnailWidth)
    );

    return transformations.toString();
  }

  public get gallery(): string {
    const transformations = CommonCloudinaryTransformer.buildBaseTransformedUrl(
      this._cloudinaryImage?.url,
      new GalleryPdpHeroTransformation(),
      new ScaleTransformation(GalleryWidth)
    );

    return transformations.toString();
  }

  public get zoom(): string {
    const transformations = CommonCloudinaryTransformer.buildBaseTransformedUrl(
      this._cloudinaryImage?.url,
      new ZoomPdpHeroTransformation(),
      new ScaleTransformation(ZoomWidth)
    );

    return transformations.toString();
  }

  private static buildBaseTransformedUrl(
    url: string | undefined,
    specificTransformation: Transformation,
    elseSpecificTransformation: Transformation
  ): TransformedUrl {
    const transformations = new TransformedUrl(url ?? '');

    const notOneConditional = new IsNotOneToOneConditional();
    notOneConditional.include(specificTransformation);
    notOneConditional.includeElse(elseSpecificTransformation);

    transformations.stack(notOneConditional);
    transformations.stack(new AdjustQualityTransformation());

    return transformations;
  }
}

export class PosterImageCloudinaryTransformer extends CommonCloudinaryTransformer {
  constructor(cloudinaryImage: string | undefined) {
    super({
      url: cloudinaryImage ?? '',
    });
  }

  public override get thumbnail(): string {
    if (this._cloudinaryImage?.url === null) {
      return '';
    }

    return super.thumbnail;
  }

  public override get gallery(): string {
    if (this._cloudinaryImage?.url === null) {
      return '';
    }

    return super.gallery;
  }

  public override get zoom(): string {
    return '';
  }

  public override get isValid(): boolean {
    return !!this._cloudinaryImage?.url;
  }
}

export class ReferentialCloudinaryTransformer extends CloudinaryTransformer {
  constructor(cloudinaryImage: IImage) {
    super(cloudinaryImage);
  }

  get thumbnail(): string {
    const transformations = ReferentialCloudinaryTransformer.buildBaseTransformedUrl(
      this._cloudinaryImage.url
    );
    transformations.stack(new ScaleTransformation(ThumbnailWidth));

    return transformations.toString();
  }

  get gallery(): string {
    const transformations = ReferentialCloudinaryTransformer.buildBaseTransformedUrl(
      this._cloudinaryImage?.url
    );
    transformations.stack(new ScaleTransformation(GalleryWidth));

    return transformations.toString();
  }

  get zoom(): string {
    return ReferentialCloudinaryTransformer.buildBaseTransformedUrl(
      this._cloudinaryImage?.url
    ).toString();
  }

  private static buildBaseTransformedUrl(url: string): TransformedUrl {
    const transformations = new TransformedUrl(url);

    const wideConditional = new IsWideConditional();
    wideConditional.include(new WideAdjustToOneToOneTransformation());

    const side43Conditional = new IsFourToThreeConditional();
    side43Conditional.include(new SideFourToThreeAdjustToOneToOneTransformation());

    transformations.stack(wideConditional);
    transformations.stack(side43Conditional);
    transformations.stack(new AdjustQualityTransformation());

    return transformations;
  }
}
